/**
 * Clip share links.
 *
 * A share link has to name two things: which bucket the clip lives in, and
 * which clip it is. Both travel *inside* the URL, so no lookup table exists
 * anywhere — the viewer site is static files and nothing of ours is written to
 * when a clip is shared. That is what keeps sharing free to run and impossible
 * to abuse as a URL shortener.
 *
 * Shape: `https://openvod.app/v?p=<bucket token>&c=<clip id>`
 *
 * This module is imported by both the desktop app (link generation) and the
 * website (link parsing), so it must stay dependency-free and side-effect-free.
 */

/** Origin of the viewer site. */
export const CLIP_SITE_ORIGIN = "https://openvod.app";

/** Viewer route on that site. */
export const CLIP_VIEWER_PATH = "/v";

/** Prefix every shared object lives under, so the bucket stays tidy. */
export const CLIP_PREFIX = "clips";

/**
 * Fixed object names under each share id. Avoids spaces/odd characters from
 * the source filename ending up in the public URL.
 */
export const CLIP_VIDEO_OBJECT = "video.mp4";
export const CLIP_SIDECAR_OBJECT = "clip.json";
export const CLIP_POSTER_OBJECT = "poster.jpg";

/**
 * Cloudflare's managed public bucket domain. Its subdomain is the only varying
 * part, so a managed bucket compresses to that token alone.
 */
const R2_DEV_SUFFIX = ".r2.dev";
const R2_DEV_PREFIX = "pub-";

/** Marker for the custom-domain form: `d.<hostname>`. */
const CUSTOM_DOMAIN_PREFIX = "d.";

/** r2.dev subdomains are a lowercase hex blob; stay lenient about the length. */
const R2_TOKEN_RE = /^[a-z0-9]{8,64}$/;

/**
 * Deliberately strict: this parses an attacker-supplied query parameter that
 * decides which origin the viewer streams from. Labels only, no userinfo, no
 * port, no path, no trailing dot.
 */
const HOSTNAME_RE = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/;

/** Share ids are `randomBytes(9).toString("base64url")`, so 12 URL-safe chars. */
const CLIP_ID_RE = /^[A-Za-z0-9_-]{6,64}$/;

const MAX_HOST_LENGTH = 253;

/** Strip protocol and any trailing slash from a stored public base URL. */
const hostOf = (publicBaseUrl: string): string | null => {
  const trimmed = publicBaseUrl.trim();
  if (!trimmed) return null;
  try {
    const url = new URL(trimmed.includes("://") ? trimmed : `https://${trimmed}`);
    return url.hostname.toLowerCase();
  } catch {
    return null;
  }
};

/**
 * Compress a bucket's public base URL into the shortest token that can be
 * expanded back to it. Returns null when the URL is unusable as a link source.
 */
export const encodePublicHost = (publicBaseUrl: string): string | null => {
  const host = hostOf(publicBaseUrl);
  if (!host || host.length > MAX_HOST_LENGTH) return null;

  if (host.startsWith(R2_DEV_PREFIX) && host.endsWith(R2_DEV_SUFFIX)) {
    const token = host.slice(R2_DEV_PREFIX.length, host.length - R2_DEV_SUFFIX.length);
    if (R2_TOKEN_RE.test(token)) return token;
  }

  if (!HOSTNAME_RE.test(host)) return null;
  return `${CUSTOM_DOMAIN_PREFIX}${host}`;
};

/**
 * Expand a `p` token back into an https origin, or null if it is malformed.
 *
 * Always forces https and rebuilds the URL from a validated hostname rather
 * than passing the token through, so a crafted `p` cannot smuggle in a
 * different scheme, credentials, or a path.
 */
export const decodePublicHost = (token: string | null | undefined): string | null => {
  if (typeof token !== "string") return null;
  const value = token.trim().toLowerCase();
  if (!value || value.length > MAX_HOST_LENGTH) return null;

  if (value.startsWith(CUSTOM_DOMAIN_PREFIX)) {
    const host = value.slice(CUSTOM_DOMAIN_PREFIX.length);
    return HOSTNAME_RE.test(host) ? `https://${host}` : null;
  }

  return R2_TOKEN_RE.test(value) ? `https://${R2_DEV_PREFIX}${value}${R2_DEV_SUFFIX}` : null;
};

/** Reject ids that could escape the `clips/<id>/` prefix or inject a path. */
export const isValidClipId = (id: string | null | undefined): id is string =>
  typeof id === "string" && CLIP_ID_RE.test(id);

/** Absolute URL of one object belonging to a share. */
export const clipObjectUrl = (origin: string, id: string, object: string): string =>
  `${origin.replace(/\/+$/, "")}/${CLIP_PREFIX}/${id}/${object}`;

/**
 * The link a user actually sends people: our domain, their bucket, no lookup.
 * Falls back to the direct bucket URL when the public host cannot be encoded,
 * so sharing never breaks just because a custom domain looks unusual.
 */
export const clipViewerUrl = (publicBaseUrl: string, id: string): string => {
  const token = encodePublicHost(publicBaseUrl);
  if (!token) return clipObjectUrl(publicBaseUrl, id, CLIP_VIDEO_OBJECT);
  const params = new URLSearchParams({ p: token, c: id });
  return `${CLIP_SITE_ORIGIN}${CLIP_VIEWER_PATH}?${params.toString()}`;
};
